import { z } from 'zod';
import { TransferActionParametersSchema } from '../models/transfer-action.js';
import { startGreetingShape } from './pre-greeting.js';
/**
 * The transfer-node primitive and the outcome events it raises. Transfer is a
 * first-class node type parallel to Say, Conversation, Form, and End
 * Conversation — not a sub-type of the generic Action primitive. The author
 * drops it on the canvas, picks the recipient from the actions catalog, and
 * draws edges out of it that route on one of the five outcomes.
 *
 * The node carries only a reference to a shared `WorkflowAction` by id. The
 * API's `assembled()` endpoint resolves that reference and embeds the
 * recipient parameters into the node JSON at dispatch time, so the worker
 * reads the ordered phone tree, declined message, and transfer instructions
 * directly off the node.
 */
/**
 * Routable transfer-node events. Public contract — a rename is a breaking
 * change. Each describes the whole phone tree, not the last number dialed:
 * one accepted attempt is a connection; otherwise a mailbox anywhere in the
 * tree is `transfer_voicemail`, then a live decline anywhere is
 * `transfer_declined`, then a ring-out anywhere is `transfer_timeout`, and
 * `transfer_failed` means every number failed to connect. `transfer_failed`
 * is also the catch-all: when the tree is exhausted and the specific outcome
 * has no edge, the runtime raises `transfer_failed` and routes on that.
 * - `transfer_completed`: the merge happened and the merged call then ended,
 *   by either side. The transfer succeeded and the call is over, so its edge
 *   must lead to an end node (or to nothing, which also ends the call).
 */
export const TRANSFER_NODE_EVENTS = [
    'transfer_declined',
    'transfer_voicemail',
    'transfer_failed',
    'transfer_timeout',
    'transfer_completed',
];
/** The names the editor shows for the transfer events. Validator messages
 *  and the editor copy share them, so an author reads one name everywhere. */
export const TRANSFER_EVENT_LABELS = {
    transfer_declined: 'Transfer declined',
    transfer_voicemail: 'Transfer reached voicemail',
    transfer_failed: 'All transfers failed',
    transfer_timeout: 'Transfer not answered',
    transfer_completed: 'Transfer success',
    transfer_connected: 'Transfer connected',
    transfer_hangup: 'Transfer hung up',
};
/**
 * Lifecycle markers published for audit only; never routable.
 * - `transfer_connected`: the merge happened. The caller is now talking to the
 *   staff member. The graph pauses here until the merged call ends.
 * - `transfer_hangup`: a participant left the merged call. `transfer_completed`
 *   follows it and is the event to route on.
 */
export const TRANSFER_LIFECYCLE_AUDIT_EVENTS = [
    'transfer_connected',
    'transfer_hangup',
];
export const transferConfigurationSchema = z.object({
    /**
     * The `WorkflowAction` row this node delegates to. Resolved by `assembled()`
     * against the company's actions catalog. Empty string is permitted so the
     * author can drop a Transfer node and wire outcomes before picking an
     * action; the API's `verifyTransferActionReferences` catches an empty or
     * cross-tenant id and returns a focused `transfer_action_not_picked`
     * issue the editor lands on.
     */
    workflowActionId: z
        .string()
        .max(64)
        .refine(value => value === '' || /^c[a-z0-9]+$/.test(value), 'workflowActionId must be a cuid or empty'),
    ...startGreetingShape,
});
export const transferNodeSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(120),
    type: z.literal('transfer'),
    /** Display order within the graph. */
    position: z.number().int().nonnegative(),
    /** Author-visible layout; see nodeLayoutMetadataSchema in graph.ts. */
    layoutMetadata: z
        .object({ position: z.object({ x: z.number(), y: z.number() }) })
        .nullable()
        .optional(),
    ...transferConfigurationSchema.shape,
    /**
     * Resolved parameters the worker reads. Attached by the API's `assembled()`
     * for the saved-graph path and by `enrichGraphForDispatch` on the client
     * for the web-dispatch (carried-graph) path. Optional because the author's
     * save-time graph does not carry it; the worker rejects a transfer node
     * that reaches dispatch without it.
     */
    parameters: TransferActionParametersSchema.optional(),
});
//# sourceMappingURL=transfer-node.js.map